import { Outlet } from 'react-router-dom';
import { useRealtime } from '@/hooks/use-realtime';
import { AccessRestrictionRedirect } from '@/components/AccessRestrictionRedirect';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth.store';

const FIELD_ROLE_SLUGS = new Set(['service-engineer', 'master-service-engineer']);

export function AppLayout() {
  const roleSlug = useAuthStore((s) => s.user?.roleSlug ?? null);
  const isFieldRole = roleSlug !== null && FIELD_ROLE_SLUGS.has(roleSlug);

  // B6.17 — ONE Socket.IO connection for the whole authenticated tenant app.
  // Mounted here, at the layout root, rather than inside any page: a page-level
  // hook would open a socket per mounted component and tear it down on every
  // navigation. Signals arriving while the user is elsewhere still invalidate
  // the relevant caches, so the OPS dashboard is already correct when opened.
  useRealtime();

  return (
    <div
      className={cn('flex h-screen overflow-hidden bg-background', isFieldRole && 'theme-field')}
    >
      {/* Routes to /access-restricted once the backend reports this workspace
          may not be used. Mounted here because AppLayout is the parent of every
          protected tenant route, so one mount covers them all. Renders nothing. */}
      <AccessRestrictionRedirect />
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header />
        {/* Footer flows inside the scroll area so it sits directly below the
            page's final section. The inner wrapper is `min-h-full flex-col` and
            the content region is `flex-1`, so on short pages the content grows to
            fill the viewport and the footer rests at the bottom (no blank band —
            neither above nor below it); on tall pages the content overflows, the
            area scrolls, and the footer follows the last section.

            `relative` makes <main> the containing block for its absolutely-
            positioned descendants. Without it, absolute children with no other
            positioned ancestor (e.g. the `sr-only` hidden file inputs inside an
            expanded Finding) are laid out against the viewport, are NOT clipped
            by this scroll container, and inflate the document height — producing
            a second (page-level) scrollbar and a blank band once a Finding with
            file inputs is expanded. */}
        <main className="relative flex-1 min-h-0 overflow-auto">
          <div className="flex min-h-full flex-col">
            <div className="flex-1 p-6">
              <Outlet />
            </div>
            <Footer />
          </div>
        </main>
      </div>
    </div>
  );
}
